import { action, computed, observable } from "mobx";
import { getCurrentCulture } from "@utils";

import EventViewStore from "@state/stores/offer/event/EventViewStore";

import { LiveOfferMenuStore } from "./LiveOfferMenuStore";

import FavoriteStore from "@state/stores/offer/shared/FavoritesStore";
import type { RootStore } from "@state/stores/types";
import { PeriodConverter, redirect, getCurrentLiveSection } from "@state";
import { Options } from "@state/stores/offer/GroupOfferStore";

export default class MultiEventViewStore extends EventViewStore {
	@observable public eventIdsRaw = "";
	@observable eventsViewMap = observable.map<string, EventViewStore>();

	public isHistoryEvent(id: string): boolean {
		return this.eventsViewMap.get(id)?.isHistoryEvent(id) ?? false;
	}

	@computed public get eventIds() {
		// slice ensures to remove first empty element because of leading '/'
		let currentIds = this.eventIdsRaw.split("/").slice(1);
		return currentIds.map((id) => id.split("-")[0]);
	}
	@computed public get isEmpty() {
		let isEveryEventViewStoreEmpty = false;
		this.eventsViewMap.forEach((store, id) => {
			if (store.eventsMap.size == 0) {
				isEveryEventViewStoreEmpty = true;
			}
		});
		return isEveryEventViewStoreEmpty;
	}

	constructor(
		rootStore: RootStore,
		liveOfferMenuStore: LiveOfferMenuStore | undefined | null,
		favoritesStore: FavoriteStore,
		options: Options = null
	) {
		super(rootStore, liveOfferMenuStore, favoritesStore, null, options);
		this.liveOfferMenuStore = liveOfferMenuStore;
		this.favoritesStore = favoritesStore;
		this.rootStore = rootStore;
	}

	@action.bound
	handleUrls(myBetsToggleValue: boolean, eventId: string) {
		const isEventIdPresent = this.eventIds.some((eId) => eId === eventId);

		if (isEventIdPresent) {
			let currentIds = this.eventIdsRaw.split("/").slice(1);
			let eventInUrl = currentIds.find((item) => item.includes(eventId));
			if (eventInUrl != null) {
				const eventInUrlSplitted = eventInUrl.split("-");
				const toggleValue = eventInUrlSplitted[1].split("=")[1];

				this.eventIdsRaw = this.eventIdsRaw.replace(
					`${eventInUrlSplitted[0]}-myBets=${toggleValue}`,
					`${eventId}-myBets=${myBetsToggleValue}`
				);

				const culture = getCurrentCulture();

				if (window.location.pathname.includes("/live/")) {
					redirect(
						`/${culture}/live/${getCurrentLiveSection()}${this.eventIdsRaw}`,
						true
					);
				} else {
					let splittedPathname = window.location.pathname
						.split("/")
						.splice(1);
					splittedPathname[4] = this.eventIdsRaw.replace("/", "");
					redirect(`/${splittedPathname.join("/")}`, true);
				}
			}
		}
	}

	@action.bound
	async onInitialize(period = PeriodConverter.GetDefaultPeriod()) {
		const isLive = window.location.pathname.includes("/live/");

		this.eventIds.forEach((eventId) => {
			const existingEventView = this.eventsViewMap.get(eventId);

			const eventView =
				existingEventView ||
				new EventViewStore(
					this.rootStore,
					this.liveOfferMenuStore,
					this.favoritesStore,
					this,
					null,
					isLive
				);

			this.eventsViewMap.set(eventId, eventView);
		});

		this.cleanUpEventViewStores();
		this.eventsViewMap.forEach((store, key) => {
			if (!store.isFetchingData && !store.isStoreInitialized) {
				store.onInitialize(key, period);
			}
		});
	}
	@action.bound
	async removeStoreFromMap(id: string) {
		this.eventsViewMap.get(id)?.onDispose();
		this.eventsViewMap.delete(id);
	}

	cleanUpEventViewStores() {
		this.eventsViewMap.forEach((_, key) => {
			if (!this.eventIds.includes(key)) {
				this.removeStoreFromMap(key);
			}
		});
	}

	@action.bound
	public onDispose(): void {
		this.disposeHubSubscriptions();
		this.eventIdsRaw = "";
		this.eventsViewMap.clear();
	}

	@action.bound
	private disposeHubSubscriptions() {
		this.eventsViewMap.forEach((store) => {
			store.onDispose();
		});
	}
}
